import { CheckCircle, ChevronDown, DollarSign, Edit, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleAdsUpdateBudget } from 'sprites-types/workflow/tools/googleAdsUpdateBudget'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function BudgetUpdateItem({ budget, className }: { budget: any; className?: string }) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const getStatusColor = (success: boolean) => {
    return success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }

  const getBudgetChangeIcon = () => {
    if (budget.newDailyBudget > budget.previousDailyBudget) {
      return <TrendingUp className="h-3 w-3 text-green-600" />
    } else if (budget.newDailyBudget < budget.previousDailyBudget) {
      return <TrendingUp className="h-3 w-3 rotate-180 text-red-600" />
    } else {
      return <DollarSign className="h-3 w-3 text-gray-500" />
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
          <Edit className="h-4 w-4 text-blue-600" />
          <div className="flex-1 text-sm font-semibold text-gray-900">Budget Updated</div>
          <div className={`rounded-full px-2 py-1 text-xs ${getStatusColor(budget.success)}`}>
            {budget.success ? 'Success' : 'Failed'}
          </div>
        </div>

        <div className="mb-3">
          <div className="text-sm font-medium text-gray-900">{budget.campaignName}</div>
          <div className="text-xs text-gray-600">Campaign ID: {budget.campaignId}</div>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <DollarSign className="h-3 w-3 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">
                {formatCurrency(budget.previousDailyBudget)}
              </div>
              <div className="text-gray-600">Previous Daily Budget</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {getBudgetChangeIcon()}
            <div>
              <div className="font-medium text-gray-900">
                {formatCurrency(budget.newDailyBudget)}
              </div>
              <div className="text-gray-600">New Daily Budget</div>
            </div>
          </div>
        </div>

        {(budget.previousTotalBudget || budget.newTotalBudget) && (
          <div className="mb-3 grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="font-medium text-gray-900">
                {budget.previousTotalBudget ? formatCurrency(budget.previousTotalBudget) : 'N/A'}
              </div>
              <div className="text-gray-600">Previous Total Budget</div>
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {budget.newTotalBudget ? formatCurrency(budget.newTotalBudget) : 'N/A'}
              </div>
              <div className="text-gray-600">New Total Budget</div>
            </div>
          </div>
        )}

        <div className="mb-3 text-xs text-gray-600">
          <div>
            Budget Type: <span className="font-medium">{budget.budgetType}</span>
          </div>
          <div>
            Updated:{' '}
            <span className="font-medium">{new Date(budget.updatedAt).toLocaleString()}</span>
          </div>
        </div>

        {budget.reason && (
          <div className="mb-3 text-xs text-gray-600">
            Reason: <span className="font-medium">{budget.reason}</span>
          </div>
        )}

        {budget.success && (
          <div className="flex items-center gap-2 border-t border-gray-200 pt-2">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="text-xs text-green-600">Budget successfully updated</span>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleAdsUpdateBudgetDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsUpdateBudget, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const budgets = Array.isArray(result) ? result : [result]

  if (budgets.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Edit className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No budgets updated</div>
        </div>
      </div>
    )
  }

  if (budgets.length === 1) {
    return <BudgetUpdateItem budget={budgets[0]} />
  }

  const successfulBudgets = budgets.filter((b: any) => b.success).length
  const totalBudgetChange = budgets.reduce(
    (sum: number, b: any) => sum + (b.newDailyBudget - b.previousDailyBudget),
    0
  )

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Edit className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Budget Update Results
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {successfulBudgets} of {budgets.length} budgets updated successfully
                {totalBudgetChange !== 0 &&
                  ` • ${totalBudgetChange > 0 ? '+' : ''}$${totalBudgetChange.toFixed(2)} total change`}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {budgets.map((budget: any, index: number) => (
            <BudgetUpdateItem key={budget.campaignId || index} budget={budget} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsUpdateBudget(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsUpdateBudgetToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsUpdateBudget, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const budgets = Array.isArray(result) ? result : [result]

  if (budgets.length === 0) {
    return `## Google Ads Budget Update Results

No budgets were updated.

---
`
  }

  const budgetsMarkdown = budgets
    .map((budget: any) => {
      return `### ${budget.campaignName}

**Campaign ID:** ${budget.campaignId}
**Budget Type:** ${budget.budgetType}
**Previous Daily Budget:** $${budget.previousDailyBudget.toFixed(2)}
**New Daily Budget:** $${budget.newDailyBudget.toFixed(2)}
${budget.previousTotalBudget ? `**Previous Total Budget:** $${budget.previousTotalBudget.toFixed(2)}` : ''}
${budget.newTotalBudget ? `**New Total Budget:** $${budget.newTotalBudget.toFixed(2)}` : ''}
**Updated At:** ${new Date(budget.updatedAt).toLocaleString()}
**Success:** ${budget.success ? 'Yes' : 'No'}
${budget.reason ? `**Reason:** ${budget.reason}` : ''}
`
    })
    .join('\n---\n\n')

  return `## Google Ads Budget Update Results (${budgets.length} budgets)

${budgetsMarkdown}

---
`
}

export const GoogleAdsUpdateBudgetDisplay = createWorkflowToolComponent(
  GoogleAdsUpdateBudgetDisplayComponent,
  downloadGoogleAdsUpdateBudget,
  googleAdsUpdateBudgetToMarkdown
)
